import { createContext, useContext, useEffect, useState } from 'react';

type Theme = 'light' | 'dark' | 'system';

interface ThemeContextType {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  resolvedTheme: 'light' | 'dark';
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  // Midnight Lockdown: Force dark mode globally
  const [resolvedTheme] = useState<'light' | 'dark'>('dark');

  useEffect(() => {
    const root = window.document.documentElement;
    // Always apply dark mode - no toggle allowed
    root.classList.remove('light', 'dark');
    root.classList.add('dark');
    // Tell the UA we are dark-only (prevents “light” form controls / scrollbars).
    root.style.colorScheme = 'dark';
    // Force background to #020617
    root.style.backgroundColor = '#020617';
    document.body.style.backgroundColor = '#020617';

    // Drift Guard: hard-lock key CSS variables to Midnight palette to prevent
    // any accidental “light” variable bleed if a component misses the `.dark` class.
    root.style.setProperty('--background', '#020617');
    root.style.setProperty('--card', 'hsl(217, 25%, 20%)'); // gunmetal
    root.style.setProperty('--muted', 'hsl(217, 25%, 20%)'); // gunmetal
    root.style.setProperty('--border', 'hsl(217, 25%, 28%)');
    root.style.setProperty('--foreground', 'hsl(210, 20%, 95%)');
  }, []);

  return (
    <ThemeContext.Provider value={{ theme: 'dark', setTheme: () => {}, resolvedTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
